import React, { useEffect, useMemo, useRef } from 'react';
import { ITimeToPage } from '../../shared/types/Stabledata.type';
import StatCard from '../../components/dashboard/StatCard';
import { t } from 'i18next';
import { Box, Card, CardContent, Chip, Container, Divider, Typography } from '@mui/material';
import palette from '../../theme/palette';
import { TinsightParam } from '../../shared/types/computedKeyParam.type';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { isEmpty, orderBy } from 'lodash';
import { Icon } from '@iconify/react';

function TimeToPage({ data, insightParams }: { data: ITimeToPage; insightParams: TinsightParam[] }) {
    const chartRef = useRef<HighchartsReactRefObject | null>(null);

    const SORTING_KEY = 'insight.time_to_100bpm_percent';
    const TIME_TO_120_BPM_KEY = 'insight.time_to_120bpm_percent'; //Temps jusqu'à 55 % de la FC max
    const TIME_TO_100_BPM_KEY = 'insight.time_to_100bpm_percent'; //Temps jusqu'à 45 % de la FC max

    const MEDIAN_120_COLOR = '#003153'
    const MEDIAN_100_COLOR = '#00674b'


    const formattedAvg = data.averages.map(avg => ({
        ...avg,
        ...(insightParams.find(param => param.name === avg.parameter_name) || {}),
    }));

    const sortedAveragesHorses = orderBy(
        data.horses_averages,
        horse => {
            const found = horse.averages.find(param => param.parameter_name === SORTING_KEY);
            return found?.raw_numeric_value ?? -Infinity;
        },
        ['desc']
    );

    const getTimeTo120Bpm = useMemo(() => {
        if (isEmpty(sortedAveragesHorses)) return [];

        return sortedAveragesHorses.map(avg => {
            const foundAvgByKey = avg.averages.find(i => i.parameter_name === TIME_TO_120_BPM_KEY);
            return foundAvgByKey ? foundAvgByKey.raw_numeric_value : 0;
        });
    }, [sortedAveragesHorses]);

    const getTimeTo100Bpm = useMemo(() => {
        if (isEmpty(sortedAveragesHorses)) return [];

        return sortedAveragesHorses.map(avg => {
            const foundAvgByKey = avg.averages.find(i => i.parameter_name === TIME_TO_100_BPM_KEY);
            return foundAvgByKey ? foundAvgByKey.raw_numeric_value : 0;
        });
    }, [sortedAveragesHorses]);

    // Remove dummy category to show all horse names including the last one
    const getHorsesNames = useMemo(() => {
        if (isEmpty(sortedAveragesHorses)) return [];
        return sortedAveragesHorses.map(avg => avg?.horse?.name || '');
    }, [sortedAveragesHorses]);

    const createPlotLinesFromMedian = (): Highcharts.YAxisPlotLinesOptions[] => {
        if (isEmpty(data.medians)) return [];

        return data.medians.map(median => ({
            value: median.raw_numeric_value,
            color: median.parameter_name === TIME_TO_100_BPM_KEY ? MEDIAN_100_COLOR : MEDIAN_120_COLOR,
            width: 2,
            dashStyle: 'Dash',
            label: {
                text: `${median.raw_numeric_value}`, // Same as Y-axis label
                align: 'left', // Align like Y-axis labels
                x: 0,
                y: 11,
                style: {
                    fontWeight: 'bold',
                    color: median.parameter_name === TIME_TO_100_BPM_KEY ? MEDIAN_100_COLOR : MEDIAN_120_COLOR,
                    fontSize: '11px' // You can match this to your yAxis.labels.style
                }
            }
        }));
    };

    const options: Highcharts.Options = {
        chart: {
            type: 'area',
            height: 550,
            animation: false,
            style: {
                overflow: 'visible',
            },
            backgroundColor: 'transparent'

        },
        title: {
            text: t('stable:title:avg_time_to'),
            align: 'left',
            style: {
                fontSize: "14px"
            }
        },
        plotOptions: {
            series: {
                pointPlacement: 'on',
                pointRange: 1,
                states: {
                    hover: {
                        enabled: false,
                    },
                    inactive: {
                        enabled: false,
                        opacity: 1,
                    },
                },
                marker: {
                    symbol: 'circle',
                },
            },
            area: {
                fillOpacity: 0.3,
                lineWidth: 2,
                marker: {
                    symbol: 'circle',
                },
            },
        },
        credits: {
            enabled: false,
        },
        xAxis: {
            plotLines: createPlotLinesFromMedian(),
            categories: getHorsesNames,
            gridLineWidth: 0,
            endOnTick: false,
            maxPadding: 0.15,
            showLastLabel: true,
            labels: {
                allowOverlap: true,
                rotation: -40,
                useHTML: true,
                formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): string {
                    // Show all labels including the last one
                    return this.value as string;
                },
                style: {
                    fontSize: '11px'
                },
                // Force show all labels
                step: 1,
            },
            // Ensure enough space for the last label
            tickmarkPlacement: 'on',
        },
        legend: {
            enabled: true,
            layout: 'vertical',
            align: 'right',         // Align to right side
            verticalAlign: 'top',   // Align to top
            x: -30,                 // Fine-tune horizontal position (negative = move left)
            y: -15,                  // Space from top edge (positive = push downward)
            floating: true,        // Set to true if you want it over the plot area
            itemMarginTop: 5,       // Space between legend items (optional)
            itemMarginBottom: 5,
            padding: 8,             // Padding inside the legend box
            symbolPadding: 8        // Space between symbol and text
        },
        yAxis: {
            plotLines: createPlotLinesFromMedian(),
            min: 0,
            max: 1000,
            gridLineWidth: 0,// disables horizontal grid lines
            title: {
                text: t('eap:table:filters:second'),
            },
        },
        tooltip: {
            enabled: false,
        },
        series: [
            {
                name: t(formattedAvg.find(e => e.parameter_name === TIME_TO_120_BPM_KEY)?.name_translated || "").concat(` (${t('firstSection:average')})`),
                data: getTimeTo120Bpm,
                type: 'area',
                color: palette.light.primary.main,
                zIndex: 2,
            },
            {
                name: t(formattedAvg.find(e => e.parameter_name === TIME_TO_100_BPM_KEY)?.name_translated || "").concat(` (${t('firstSection:average')})`),
                data: getTimeTo100Bpm,
                type: 'area',
                color: palette.light.primary.light,
                zIndex: 0,
            },
            {
                name: t(formattedAvg.find(e => e.parameter_name === TIME_TO_100_BPM_KEY)?.name_translated || "").concat(` (${t('stable:key:median')})`),
                data: [],
                type: 'line',
                color: MEDIAN_100_COLOR,
                zIndex: 0,
                dashStyle: 'ShortDot', // dotted line
                marker: { enabled: false }, // no circle in chart or legend
            },
            {
                name: t(formattedAvg.find(e => e.parameter_name === TIME_TO_120_BPM_KEY)?.name_translated || "").concat(` (${t('stable:key:median')})`),
                data: [],
                type: 'line',
                color: MEDIAN_120_COLOR,
                zIndex: 0,
                dashStyle: 'ShortDot', // dotted line
                marker: { enabled: false }, // no circle in chart or legend
            },
        ],
    };

    const getParam = (param: string) => {
        return data.horse_best_records.find(
            br => br.parameter_value.parameter_name === param
        );
    }

    return (
        <>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
                <Typography variant="h4" sx={{ color: palette.light.primary.main }}>
                    {t('stable:title:'.concat(data.type))}
                </Typography>
                <img
                    style={{ objectFit: 'contain', width: 55, height: 55, marginRight: 15 }}
                    src="/arioneo_logo_white.png"
                    alt="arioneo logo"
                />
            </Box>
            <Card sx={{ mb: 3 }} >

                <CardContent>
                    <Typography variant='h5'>{t('dataParameters:groups:averages')}</Typography>
                    <Box display="flex" justifyContent="space-evenly" alignItems="center" gap={2} mt={1}>
                        {formattedAvg.map(avg => {
                            const found = getParam(avg.parameter_name)
                            return (
                                <StatCard
                                    key={avg.parameter_name}
                                    icon={avg.icon}
                                    title={avg.name_translated || ''}
                                    value={avg.formatted_value.concat(` ${avg.unit_translated}`)}
                                    isLoading={false}
                                >
                                    {found ? (
                                        <Box display="flex" gap={.5} alignItems={'center'}  >
                                            <Icon width={20} height={20} icon={'fluent-color:trophy-16'} />
                                            <Divider flexItem orientation='vertical' />

                                            <Box display={'flex'} flexDirection={'column'} alignItems={'self-start'}>
                                                <Typography color="#808080" variant="subtitle2" fontSize="15px">
                                                    {found.parameter_value.formatted_value}
                                                </Typography>
                                                <Typography variant="caption" fontSize="13px">
                                                    {found.horse?.name}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ) : null}
                                </StatCard>
                            );
                        })}
                    </Box>
                </CardContent>
            </Card>
            <div style={{ width: '1200px' }} id="chart-container">
                <HighchartsReact ref={chartRef} highcharts={Highcharts} options={options} />
            </div>
        </>
    );
}

export default TimeToPage;